import { AbstractGameState } from "@/app/lib/game-state/abstract-game-state";
import { StoryLevelData, Phase } from "@/app/lib/levels/story-level-data-types";
import { GameMode } from "@/app/lib/game-state/game-types";
import WordModule from "@/app/lib/game-state/word-module";
import * as Util from "@/app/lib/system/util";

export type StorySnapshot = {
    words: string[],
    solved: Array<{
        wordIndex: number,
        solution: string
    }>,
    usedSolutions: string[];
    completed: boolean,
    victorious: boolean,
    version: string,
    currentPhaseIndex: number,
    phases: Phase[],
    levelNumber: number,
    levelName: string
    levelID: string
}

export class StoryGameState extends AbstractGameState<StoryLevelData, StorySnapshot> {
    
    name: GameMode = 'story';
    currentPhase: Phase = StoryLevelData.createDummyPhase();
    phases: Phase[] = [];
    
    levelNumber: number = 0;
    levelName: string = '';
    levelID: string = '';

    constructor(version: string) {
        super(version)
    }

    protected wipeSpecific(): void {
        this.currentPhase = StoryLevelData.createDummyPhase();
        this.usedSolutions = [];
        this.phases = [];
        this.levelNumber = 0;
    }

    buildPuzzle(storyLevelData: StoryLevelData): void {
        this.wipe();
        this.levelNumber = storyLevelData.levelNumber;
        this.levelName = storyLevelData.levelName;
        this.levelID = storyLevelData.levelID;

        // the storyLevelData has the phase information already
        this.phases = storyLevelData.phases;

        // we need to add words like this so they convert to word modules
        for (const word of storyLevelData.words) {
            this.addNewWord(word);
        }

        // set phase
        this.setCurrentPhase(0); 
        this.checkPhaseComplete();
    }

    exportSnapshot() : StorySnapshot {
        // convert wordmodules to word strings
        // record solved states
        const words: string[] = [];
        const solved: Array<{
            wordIndex: number,
            solution: string
        }> = [];
        for (const wordModule of this.words) {
            words.push(wordModule.stringifyComplete());
            if (wordModule.solved) {
                solved.push({
                    wordIndex: wordModule.index,
                    solution: wordModule.stringifyState()
                })
            }
        }
        const snapshot: StorySnapshot = {
            words: words,
            solved: solved,
            usedSolutions: this.usedSolutions,
            completed: this.completed,
            victorious: this.victorious,
            version: this.version,
            currentPhaseIndex: this.currentPhase.index,
            phases: this.phases,
            levelNumber: this.levelNumber,
            levelName: this.levelName,
            levelID: this.levelID            
        }
        return snapshot;
    }

    importSnapshot(snapshot: StorySnapshot): void {
        this.wipe();
        // build the word module array
        for (const word of snapshot.words) {
            this.addNewWord(word);
        }
        // process any solves
        for (const solvedWord of snapshot.solved) {
            const wordModule: WordModule = this.words[solvedWord.wordIndex];
            wordModule.import(solvedWord.solution);
            wordModule.solved = true;
        }

        // read the rest of the data
        this.usedSolutions = snapshot.usedSolutions;
        this.completed = snapshot.completed;
        this.victorious = snapshot.victorious;
        this.version = snapshot.version;
        this.phases = snapshot.phases;
        this.levelNumber = snapshot.levelNumber;
        this.levelName = snapshot.levelName;
        this.levelID = snapshot.levelID;

        // set the current phase
        this.setCurrentPhase(snapshot.currentPhaseIndex);
    }

    // static lets us call without creating an instance
    // null inputs allow us to perform the null checks as we validate
    static validateSnapshot(snapshot: StorySnapshot | null, levelData: StoryLevelData | null) : boolean {
        // if either is null return fals
        if (snapshot === null || levelData === null) {
            Util.devLog('validate snapshot exit 1')
            return false;
        }

        // we only care about levelID, not name or level number
        if (snapshot.levelID !== levelData.levelID) {
            Util.devLog('validate snapshot exit 2')
            return false;
        }

        // check that we have same number of phases in each
        if (snapshot.phases.length !== levelData.phases.length) {
            Util.devLog('validate snapshot exit 3')
            return false;
        }

        // compare the words list in its entirety, order matters
        if (!Util.compareSimpleArrays(
            snapshot.words,
            levelData.words
        )) {
            Util.devLog('validate snapshot exit 4')
            return false;
        }

        // compare each phase
        for (let i = 0; i < snapshot.phases.length; i++) {
            // compare the ingredients -- order in arrays does not matter
            if (!Util.compareSimpleArrays(
                snapshot.phases[i].ingredients,
                levelData.phases[i].ingredients,
                false // order does not matter
            )) {
                Util.devLog('validate snapshot exit 5')
                return false;
            }
            // compare the wordIndices -- in theory they are ordered, but it shouldn't matter
            if (!Util.compareSimpleArrays(
                snapshot.phases[i].wordIndices,
                levelData.phases[i].wordIndices,
                false
            )) {
                Util.devLog('validate snapshot exit 6')
                return false;
            }
            // compare the solutions accepted, again order shouldn't matter
            if (!Util.compareSimpleArrays(
                snapshot.phases[i].altSolutions,
                levelData.phases[i].altSolutions,
                false
            )) {
                Util.devLog('validate snapshot exit 7')
                return false;
            }
            // now we have to check the solutions in the snapshot
            // first make sure we have the right amount
            if (snapshot.solved.length !== snapshot.usedSolutions.length) {
                Util.devLog('validate snapshot exit 8')
                return false;
            }
            // now we check that each solution is valid
            for (const solve of snapshot.solved) {
                // get the word and verify that it has been registered in used solutions
                if (!snapshot.usedSolutions.includes(solve.solution.toLowerCase())) {
                    Util.devLog('validate snapshot exit 9')
                    return false;
                }
                // get the word's phase
                const phase: Phase | null = this.getPhaseFromWordIndex(
                    solve.wordIndex,
                    snapshot.phases
                );
                if (!phase) {
                    Util.devLog('validate snapshot exit 10')
                    return false;
                }
                // make sure the solve word is a puzzle word in phase or in phase solutions
                const allSolutions: string[] = [];
                for (const wordIndex of phase.wordIndices) {
                    const word: string = snapshot.words[wordIndex].toLowerCase();
                    allSolutions.push(word);
                }
                allSolutions.push(...phase.altSolutions);
                if (!allSolutions.includes(solve.solution.toLowerCase())) {
                    Util.devLog('validate snapshot exit 11')
                    return false;
                }
            }
        }
        // check that current phase is kosher
        if (
            snapshot.currentPhaseIndex < 0
            || snapshot.currentPhaseIndex > levelData.phases.length - 1
        ) {
            Util.devLog('validate snapshot exit 12')
            return false;
        }
        // all phases before the current must be totally solved
        for (let i = 0; i < snapshot.currentPhaseIndex; i++) {
            for (const wordIndex of snapshot.phases[i].wordIndices) {
                if (!snapshot.solved.some(entry => entry.wordIndex === wordIndex)) {
                    Util.devLog('validate snapshot exit 13')
                    return false;
                }
            }
        }
        // we made it through the gauntlet
        return true;
    }

    static isStorySnapshot(obj: any): obj is StorySnapshot {
        if (typeof obj !== 'object' || obj === null) {
            Util.devLog('isStorySnapshot exit 1');
            return false;
        }

        // words: string[]
        if (!Array.isArray(obj.words) || !obj.words.every((w: any) => typeof w === 'string')) {
            Util.devLog('isStorySnapshot exit 2');
            return false;
        }

        // solved: { wordIndex: number, solution: string }[]
        if (
            !Array.isArray(obj.solved) ||
            !obj.solved.every((entry : any) =>
                typeof entry === 'object' &&
                entry !== null &&
                typeof entry.wordIndex === 'number' &&
                typeof entry.solution === 'string'
            )
        ) {
            Util.devLog('isStorySnapshot exit 3');
            return false;
        }

        // usedSolutions: string[]
        if (
            !Array.isArray(obj.usedSolutions) ||
            !obj.usedSolutions.every((s: any) => typeof s === 'string')
        ) {
            Util.devLog('isStorySnapshot exit 4');
            return false;
        }

        // completed: boolean
        if (typeof obj.completed !== 'boolean') {
            Util.devLog('isStorySnapshot exit 5');
            return false;
        }

        // victorious: boolean
        if (typeof obj.victorious !== 'boolean') {
            Util.devLog('isStorySnapshot exit 6');
            return false;
        }

        // version: string
        if (typeof obj.version !== 'string') {
            Util.devLog('isStorySnapshot exit 7');
            return false;
        }

        // currentPhaseIndex: number
        if (typeof obj.currentPhaseIndex !== 'number') {
            Util.devLog('isStorySnapshot exit 8');
            return false;
        }

        // phases: Phase[]
        if (
            !Array.isArray(obj.phases) ||
            !obj.phases.every((p: any) => StoryLevelData.isPhase(p))
        ) {
            Util.devLog('isStorySnapshot exit 9');
            return false;
        }

        // levelNumber: number
        if (typeof obj.levelNumber !== 'number') {
            Util.devLog('isStorySnapshot exit 10');
            return false;
        }

        // levelName: string
        if (typeof obj.levelName !== 'string') {
            Util.devLog('isStorySnapshot exit 11');
            return false;
        }

        // levelID: string
        if (typeof obj.levelID !== 'string') {
            Util.devLog('isStorySnapshot exit 12');
            return false;
        }

        return true;
    }

    setPhaseByWordIndex(wordIndex: number) {
        if (wordIndex < 0 || wordIndex > this.words.length - 1)
            return;

        // get the phase
        const phaseToSet = this.getPhaseFromWordIndex(wordIndex);
        if (phaseToSet) {
            this.setCurrentPhase(phaseToSet.index);
        }
    }


    setCurrentPhase(index: number) {
        // make sure we're in bounds
        if (index < 0 || index > this.phases.length - 1)
            return;

        // Get the current phase
        this.currentPhase = this.phases[index];
        // Set the ingredients
        this.ingredients = this.currentPhase.ingredients;

        // iterate through words and make sure their activity is set properly
        for (const word of this.words) {
            if (this.currentPhase.wordIndices.includes(word.index)) {
                word.active = true;
            } else {
                word.active = false;
            }
        }
    }

    incrementPhase() {
        if (this.victorious || this.completed)
            return;

        this.setCurrentPhase(this.currentPhase.index + 1);
        this.checkPhaseComplete();
    }

    freezeInputs(): boolean {
        if (this.victorious || this.completed)
            return true;

        return false;
    }

    getPhaseFromWordIndex(wordIndex: number) : Phase | null {
        return StoryGameState.getPhaseFromWordIndex(wordIndex, this.phases);
    }

    // useful for this to be static for static validator method
    // will look into refactoring outside class later
    static getPhaseFromWordIndex(wordIndex: number, phases: Phase[]): Phase | null {
        for (const phase of phases) {
            if (phase.wordIndices.includes(wordIndex)) {
                return phase;
            }
        }
        return null;
    }

    // For Story Mode we check against the words in the phase
    validateWord(wordIndex: number): boolean {
        const wordModule: WordModule = this.words[wordIndex];
        if (!wordModule.isComplete()) {
            return false;
        }

        const wordString: string = wordModule.stringifyState().toLowerCase();
        // check if we used it already
        if (this.usedSolutions.includes(wordString)) {
            return false;
        }

        // check it against the solutions in phase
        const phase = StoryGameState.getPhaseFromWordIndex(wordIndex, this.phases);
        if (!phase) {
            return false;
        }
        const allSolutions: string[] = [];
        // all relevant puzzle words count as a solution
        for (const wordIndex of phase.wordIndices) {
            const wordModule: WordModule = this.words[wordIndex];
            allSolutions.push(wordModule.puzzleWord);
        }
        // add any alt solutions
        allSolutions.push(...phase.altSolutions);
        // make sure our solution exists
        if (!allSolutions.includes(wordString)) {
            return false;
        }

        // if we're down here it's valid
        wordModule.solved = true;
        this.usedSolutions.push(wordString);
        this.checkPhaseComplete();
        this.checkVictory(true);
        return true;;
    }

    checkPhaseComplete() {
        if (this.victorious || this.completed)
            return;

        for (const wordIndex of this.currentPhase.wordIndices) {
            if (this.words[wordIndex].solved === false) {
                return;
            }
        }
        // if we made it down here they were all solved
        this.checkVictory(true);
        this.incrementPhase();
        return;
    }

    phaseIsComplete() : boolean {
        for (const wordIndex of this.currentPhase.wordIndices) {
            if (this.words[wordIndex].solved === false) {
                return false;
            }
        }
        return true;
    }

    reveal(trueVictory: boolean) {
        // we can naively reaveal the puzzle words
        for (const wordModule of this.words) {
            wordModule.reveal(); // no arg means reveal puzzle word
        }
    }
}